import {useState} from 'react'
import {ModalBase} from '../../../../components/Modals/ModalBase'
import {useDispatch, useSelector} from 'react-redux'
import {State} from '../../../../reducers'
import {esNormativaV2} from '../../../../utils/normativa.utils'
import {COLUMNA_DISTRITO_CLIENTES} from '../../../../utils/cargas.utils'
import {generalShowLoader} from '../../../../actions/general.actions'
import {setDataFileClientes} from '../../../../actions/gestion-cliente/agregar-cliente/agregarClienteFile.actions'
import {
  validRowClientes,
  mapeoRowClientesXLSX,
  normalizeDataKeys,
} from '../../../../utils/cargas.utils'
import * as XLSX from 'xlsx'

const MAX_FILAS = 1000

export default function ModalCargarFileClientes({
  onClose,
  onAccept,
}: {
  onClose: () => void
  onAccept: () => void
}) {
  const dispatch = useDispatch()
  const datosEmisor = useSelector((state: State) => state.catalogos.datosEmisor)
  const normativaV2 = esNormativaV2(datosEmisor)

  const [fileName, setFileName] = useState('')
  const [error, setError] = useState('')
  const [warnFilas, setWarnFilas] = useState('')
  const [dataCargada, setDataCargada] = useState<any[]>([])

  const limpiarEstado = () => {
    setFileName('')
    setError('')
    setWarnFilas('')
    setDataCargada([])
  }

  const onChangeFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      dispatch(generalShowLoader(true))
      setError('')
      setWarnFilas('')

      const file = e.target.files?.[0]
      if (!file) return

      const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase()
      if (extension !== '.xlsx') {
        setError(
          'El archivo debe tener formato .xlsx. Descarga la plantilla para usarla correctamente.'
        )
        return
      }

      setFileName(file.name)

      const data = await leerArchivoXLSX(file)
      const normalizado = normalizeDataKeys(data)
      const [validos, filasInvalidas] = filtrarFilas(normalizado)

      if (filasInvalidas.length > 0) {
        setWarnFilas(
          `Alerta: Se detectaron filas sin los datos obligatorios (fila #${filasInvalidas.join(
            ', #'
          )}). ` +
            `Los campos "Tipo de Documento", "Numero de Documento" y "Nombre del Cliente" son requeridos.`
        )
      }

      if (validos.length === 0) {
        setError(
          'No se encontraron filas válidas en el archivo. Verifica que uses la plantilla correcta.'
        )
        setDataCargada([])
        return
      }

      const mapeados = mapeoRowClientesXLSX(validos)
      setDataCargada(mapeados)
    } catch {
      setError('Ocurrió un error al leer el archivo. Verifica que el formato sea correcto.')
    } finally {
      dispatch(generalShowLoader(false))
    }
  }

  const leerArchivoXLSX = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (evt) => {
        try {
          const bstr = evt.target?.result
          const wb = XLSX.read(bstr, {type: 'binary'})
          const ws = wb.Sheets[wb.SheetNames[0]]
          resolve(XLSX.utils.sheet_to_json(ws))
        } catch {
          reject(new Error('Error al parsear el archivo XLSX'))
        }
      }
      reader.onerror = () => reject(new Error('Error al leer el archivo'))
      reader.readAsBinaryString(file)
    })
  }

  const filtrarFilas = (data: any[]): [any[], number[]] => {
    const validos: any[] = []
    const invalidos: number[] = []

    data.forEach((row, index) => {
      const [esValido] = validRowClientes(row)
      if (esValido) {
        validos.push(row)
      } else {
        invalidos.push(index + 2) // +2: encabezado (fila 1) + índice base 0
      }
    })

    return [validos, invalidos]
  }

  const handleAceptar = () => {
    if (!dataCargada.length) {
      setError('Por favor selecciona un archivo válido antes de continuar.')
      return
    }

    if (dataCargada.length > MAX_FILAS) {
      setError(
        `El archivo supera el límite de ${MAX_FILAS} filas por carga. Divide el archivo en partes más pequeñas.`
      )
      return
    }

    dispatch(setDataFileClientes(dataCargada))
    onAccept()
  }

  return (
    <ModalBase>
      <div className='dte-send-email-container'>
        <div>
          <h3 className='card-title'>Cargar clientes desde plantilla</h3>
        </div>
        <div className='separador' />
        <p className='m' style={{fontWeight: '400', margin: '0'}}>
          Selecciona el archivo <strong>.xlsx</strong> completado con la plantilla de clientes. Si
          no tienes la plantilla, usa el botón <strong>Descargar plantilla</strong> del formulario.
        </p>
        <p className='m' style={{fontWeight: '400', margin: '0'}}>
          <strong>Nota:</strong> Los campos obligatorios son: Tipo de Documento, Numero de Documento
          y Nombre del Cliente. El archivo admite un máximo de {MAX_FILAS} filas por carga.
        </p>
        <p className='m' style={{fontWeight: '400', margin: '0', color: '#555'}}>
          Si desea emitir documentos como Crédito Fiscal (CCF) u otros que requieren información
          completa, se recomienda llenar todos los campos de la plantilla. De lo contrario, deberá
          completar los datos del cliente al momento de emitir el documento.
        </p>
        {normativaV2 && (
          <p className='m' style={{fontWeight: '400', margin: '0', color: '#555'}}>
            <strong>Normativa 2.0:</strong> la plantilla trae la columna{' '}
            <strong>{COLUMNA_DISTRITO_CLIENTES}</strong>. No es obligatoria para cargar, así que una
            plantilla que usted ya tenía guardada sigue funcionando; si la deja vacía, se le pedirá
            el distrito al emitir el documento.
          </p>
        )}

        <div className='w-layout-hflex h-inputs'>
          <div className='label-input'>
            <div className='file-input'>
              <input
                autoComplete='off'
                type='file'
                className='text-field w-input upload_file_input file-input'
                name='clientesexcel'
                id='clientesexcel'
                accept='.xlsx'
                onChange={onChangeFile}
              />
              <span className='m button'>Seleccionar archivo</span>
              <span className='label'>{fileName || 'Ningún archivo seleccionado (.xlsx)'}</span>
            </div>
            {error && <p className='alert s'>{error}</p>}
          </div>
        </div>

        {warnFilas && (
          <p className='alert s' style={{whiteSpace: 'pre-line'}}>
            {warnFilas}
          </p>
        )}

        {dataCargada.length > 0 && !error && (
          <p className='m' style={{margin: '8px 0 0', color: '#2d6a4f'}}>
            <strong>{dataCargada.length}</strong> registro(s) válido(s) listos para previsualizar.
          </p>
        )}

        <div className='dte-send-email-footer' style={{marginTop: '16px'}}>
          <button className='button transparent w-inline-block' type='button' onClick={onClose}>
            Cancelar
          </button>
          <button className='button w-inline-block' type='button' onClick={handleAceptar}>
            Previsualizar datos
          </button>
        </div>
      </div>
    </ModalBase>
  )
}
